import * as React from "react";

import { ApiProperty, ApiType } from "./ComponentApiType";

import { IApiProperty, IApiType } from "./ComponentApiTypes";

import { apiFileMap } from "../_Generated/Apis";
import { components } from "../_Generated/Pages";

export interface IApiTabContentProps {
    /**
     * A set of file names containing api type defintions.
     */
    sources: string[];
}

export default class ComponentApiContent extends React.Component<IApiTabContentProps> {
    public render(): JSX.Element {
        const { sources } = this.props;
        const types: IApiType[] = [];

        sources.map((source: string) => types.push(...apiFileMap[source]));

        return (
            <div className="component-api-content">
                {types.map((type, index) => {
                    let properties = [...type.properties];
                    this.addExternalProperties(type, properties);
                    if (!type.enum) {
                        properties = properties.sort((a: IApiProperty, b: IApiProperty) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
                    }
                    if (properties.length === 0) {
                        return null;
                    }
                    return (
                        <ApiType key={index} typeName={type.name}>
                            {properties.map((property, propertyIndex) => {
                                const typeNames = property.type.split(/\s+\|\s+/);
                                const propertyLinks: string[] = [];
                                typeNames.forEach((typeName: string) => {
                                    // check if property is local.
                                    if (types.findIndex(t => t.name === typeName) > -1) {
                                        propertyLinks.push("#api-type-" + typeName);
                                        return;
                                    }
                                    // check if property exists elsewhere on the site.
                                    const regex = new RegExp("(^[A-Z].+?)(?:<.*>)?(?:\\[\\])?$", "g");
                                    const regexResult = regex.exec(typeName);
                                    if (regexResult !== null) {
                                        const fileNames = Object.keys(apiFileMap);
                                        fileNames.forEach(fileName => {
                                            // find the file name which contains the component definition.
                                            if (apiFileMap[fileName].findIndex(api => api.name.replace(/<.*>/, "") === regexResult[1]) > -1) {
                                                // find which component contains fileName in its apis list.
                                                const componentIndex = components.findIndex(
                                                    component => !!component.apis && component.apis.findIndex(api => api === fileName) > -1
                                                );
                                                if (componentIndex > -1) {
                                                    propertyLinks.push(".." + components[componentIndex].url + "#api-type-" + regexResult[1]);
                                                    return;
                                                }
                                            }
                                        });
                                    }
                                    propertyLinks.push("");
                                });

                                return (
                                    <ApiProperty
                                        key={propertyIndex}
                                        typeName={type.name}
                                        name={property.name}
                                        defaultValue={property.defaultValue}
                                        propertyTypes={typeNames}
                                        propertyTypeLinks={propertyLinks}
                                        description={property.description}
                                        optional={property.optional}
                                        deprecated={property.deprecated}
                                        deprecatedMessage={property.deprecatedMessage}
                                        inheritedFrom={property.inheritedFrom}
                                    />
                                );
                            })}
                        </ApiType>
                    );
                })}
            </div>
        );
    }

    private findType(typeName: string): IApiType | undefined {
        const typeSets = Object.keys(apiFileMap).map(k => apiFileMap[k]);
        let type: IApiType | undefined;
        for (const typeSet of typeSets) {
            type = typeSet.find(apiType => apiType.name.replace(/<.*>/, "") === typeName.replace(/<.*>/, ""));
            if (type) {
                break;
            }
        }
        return type;
    }

    private addExternalProperties(type: IApiType, properties: IApiProperty[]) {
        type.externalTypes.forEach(externalType => {
            const resolvedType = this.findType(externalType.name);
            if (resolvedType) {
                if (externalType.properties) {
                    const externalProperties = resolvedType.properties.filter(property =>
                        externalType.properties!.find(externalProperty => externalProperty === property.name)
                    );
                    this.addProperties(externalProperties, properties, resolvedType.name);
                } else {
                    this.addProperties(resolvedType.properties, properties, resolvedType.name);
                }
                this.addExternalProperties(resolvedType, properties);
            }
        });
    }

    private addProperties(newProperties: IApiProperty[], existingProperties: IApiProperty[], inheritedFrom: string) {
        newProperties.forEach(newProperty => {
            if (!existingProperties.find(existingProperty => newProperty.name === existingProperty.name)) {
                existingProperties.push({ ...newProperty, inheritedFrom });
            }
        });
    }
}
